import React, {useEffect, useMemo, useState} from "react";
import {Surface, useTheme} from "tmwx-design-web/src";
import {MaterialReactTable, MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable} from "material-react-table";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "gui-common/redux/store";
import {useTranslation} from "react-i18next";
import {usePermissions} from "tmwx-design-common";
import Text from "tmwx-design-web/src/components/Text";
import Popup from "tmwx-design-web/src/components/Popup";
import Button from "tmwx-design-web/src/components/Button";
import {AddRegisterRequest, EditRegisterRequest, Register} from "gui-common/redux/data/register";
import {
    clearRegisterResponse,
    deleteRegister,
    editRegister,
    getRegisterList,
    newRegister
} from "gui-common/redux/action/efe/registerActions";
import DeleteConfirmation from "../DeleteConfirmation";
import {setSnackbarData} from "gui-common/redux/action/snackbarAction";
import {ResponseBase} from "gui-common/redux/data";
import {isEmpty} from "gui-common/appHelper";
import {Alert} from "@mui/material";

type RegisterTestTableProps = {
    podTag: string;
    deviceType: string;
};

const RegisterTestTable = ({ podTag, deviceType }: RegisterTestTableProps) => {
    const {t} = useTranslation()
    const {padding, roundness} = useTheme()
    const dispatch: any = useDispatch()
    const {hasRights} = usePermissions()
    const loading = useSelector<RootState, boolean>(state => state.register.loading);
    const registerList = useSelector<RootState, Register[]>(state => state.register.registerList);
    const [validationFailed, setValidationFailed] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
    const [selectedRegister, setSelectedRegister] = useState<Register | null>(null)
    const [editedRegisters, setEditedRegisters] = useState<Record<string, Register>>({});
    const response = useSelector<RootState, ResponseBase | null>(state => state.register.response);

    const handleDelete = (reg: any) => {
        setSelectedRegister(reg)
        setDeleteDialogOpen(true)
    }

    const closeDeleteDialog = () => {
        setSelectedRegister(null)
        setDeleteDialogOpen(false)
    }

    const deleteReg = () => {
        if (selectedRegister) {
            dispatch(deleteRegister(selectedRegister.regId))
        }
    }

    /* HANDLE ADD, EDIT AND DELETE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                closeDeleteDialog()
                if (hasRights(["efe_registers_view"]) && !isEmpty(podTag)) {
                    dispatch(getRegisterList(podTag))
                }

                dispatch(clearRegisterResponse())
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))
            } else {
                dispatch(setSnackbarData({
                    status: 'danger',
                    text: t('actionFailed')
                }))
            }
        }
    }, [response])

    //CREATE action
    const handleCreateRegister: MRT_TableOptions<Register>['onCreatingRowSave'] = async ({
        values,
        table,
    }) => {
        const obj: AddRegisterRequest = {
            regAddress: values.regAddress ?? '',
            regByteOrder: values.regByteOrder ?? '',
            regDataType: values.regDataType ?? '',
            regDescription: values.regDescription ?? '',
            regReadOnce: values.regReadOnce ?? null,
            regRegisterCount: values.regRegisterCount ?? null,
            regRegisterGroup: values.regRegisterGroup ?? '',
            regRegisterType: values.regRegisterType ?? '',
            regScaleFactor: values.regScaleFactor ?? '',
            regUnitOfMeasurement: values.regUnitOfMeasurement ?? '',
            regWordOrder: values.regWordOrder ?? '',
            regName: values.regName ?? '',
        };

        dispatch(newRegister(podTag, obj));
        table.setCreatingRow(null);
    };

    //UPDATE action
    const handleSave = async () => {
        if (validationFailed) return;

        Object.values(editedRegisters).forEach((register) => {
            const obj: EditRegisterRequest = {
                regId: register.regId,
                regName: register.regName ?? '',
                regAddress: register.regAddress ?? '',
                regByteOrder: register.regByteOrder ?? '',
                regDataType: register.regDataType ?? '',
                regDescription: register.regDescription ?? '',
                regReadOnce: register.regReadOnce ?? null,
                regRegisterCount: register.regRegisterCount ?? null,
                regRegisterGroup: register.regRegisterGroup ?? '',
                regRegisterType: register.regRegisterType ?? '',
                regScaleFactor: register.regScaleFactor ?? '',
                regUnitOfMeasurement: register.regUnitOfMeasurement ?? '',
                regWordOrder: register.regWordOrder ?? '',
            };
            console.log(obj)

            dispatch(editRegister(obj));
        });

        setEditedRegisters({});
        setValidationFailed(false);
    };

    const columns = useMemo<MRT_ColumnDef<Register>[]>(
        () => [
            {
                accessorKey: 'regName',
                header: t('efe.register.name'),
                muiEditTextFieldProps: ({ cell, row }) => {
                    const editedValue =
                        editedRegisters[row.id]?.regName ?? cell.getValue<string>() ?? '';

                    const isInvalid =
                        !!editedRegisters[row.id] && editedValue.trim() === '';

                    return {
                        type: 'text',
                        inputProps: { maxLength: 100 },
                        required: true,
                        error: isInvalid,
                        helperText: isInvalid ? t('mandatoryField') : undefined,
                        sx: {
                            backgroundColor: isInvalid ? 'rgba(255, 0, 0, 0.06)' : 'inherit',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isInvalid ? 'error.main' : undefined,
                            },
                        },
                        onChange: (event) => {
                            const newValue = event.target.value;
                            const columnId = cell.column.id as keyof Register;

                            setEditedRegisters((prev) => ({
                                ...prev,
                                [row.id]: {
                                    ...(prev[row.id] ?? row.original),
                                    [columnId]: newValue,
                                },
                            }));

                            setValidationFailed(newValue.trim() === '');
                        },
                    };
                },
            },
            {
                accessorKey: 'regAddress',
                header: t('efe.register.address'),
                maxSize: 50,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    inputProps: { maxLength: 50 },
                    required: true,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'regUnitOfMeasurement',
                header: t('efe.register.unitOfMeasurement'),
                editVariant: 'select',
                editSelectOptions: [
                    'V','Hz','CosPhi','Kw','bitfield','kvarh','pct','var','enum','W','kWh','A','date','pct WMax'
                ],
                required: true,
                maxSize: 30,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    required: true,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'regDataType',
                header: t('efe.register.dataType'),
                editVariant: 'select',
                editSelectOptions: [
                    'Float', 'Uint8', 'String', 'Uint16', 'Int16'
                ],
                required: true,
                maxSize: 30,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    required: true,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'regRegisterType',
                header: t('efe.register.registerType'),
                editVariant: 'select',
                editSelectOptions: [
                    'int16','int32','uint16','uint16_t','uint32','string_ascii','float32','fix_uint16_div10','fix_uint16_div100','fix_uint32_div10','hexaString','register_type'
                ],
                required: false,
                maxSize: 30,
                enableHiding: true,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    required: false,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'regRegisterCount',
                header: t('efe.register.registerCount'),
                maxSize: 30,
                enableHiding: true,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'number',
                    required: false,
                    inputProps: { min: 0, max: 50 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'regRegisterGroup',
                header: t('efe.register.registerGroup'),
                editVariant: 'select',
                editSelectOptions: ['coil', 'contact', 'holding', 'input'],
                maxSize: 30,
                enableHiding: true,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    required: false,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'regReadOnce',
                header: t('efe.register.readOnce'),
                maxSize: 30,
                enableHiding: true,
                editVariant: 'select',
                editSelectOptions: [
                    'true', 'false'
                ],
            },
            {
                accessorKey: 'regDescription',
                header: t('efe.register.desc'),
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    required: true,
                    inputProps: { maxLength: 150 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'regByteOrder',
                header: t('efe.register.byteOrder'),
                editVariant: 'select',
                editSelectOptions: ['little', 'big'],
                maxSize: 30,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    required: false,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'regWordOrder',
                header: t('efe.register.wordOrder'),
                editVariant: 'select',
                editSelectOptions: ['little', 'big'],
                maxSize: 30,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    required: false,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'regScaleFactor',
                header: t('efe.register.scaleFactor'),
                maxSize: 30,
                enableHiding: true,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    required: false,
                    inputProps: { maxLength: 20 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRegisters((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
        ],
        [dispatch, hasRights, padding, t],
    );


    const table = useMaterialReactTable({
        localization: {
            noRecordsToDisplay: t('noData'),
        },
        columns,
        data: registerList,
        getRowId: (row) =>
            row.regId != null
                ? String(row.regId)
                : `temp-${row.regName ?? Math.random()}`,
        initialState: {
            density: 'compact',
            pagination: {
                pageSize: 25,
                pageIndex: 0,
            },
        },
        state: {
            isLoading: loading,
            showProgressBars: loading,
            columnVisibility: {
                regRegisterType: deviceType !== 'METER',
                regRegisterCount: deviceType !== 'METER',
                regRegisterGroup: deviceType !== 'METER',
                regReadOnce: deviceType !== 'METER',
                regScaleFactor: deviceType !== 'METER',
            },
        },
        createDisplayMode: 'row',
        editDisplayMode: 'table',
        enableEditing: true,
        enableRowActions: true,
        positionActionsColumn: 'last',
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: t('actions'),
            },
        },
        // getRowId: (row) => row.ptcId,
        // muiToolbarAlertBannerProps: isLoadingUsersError
        //     ? {
        //         color: 'error',
        //         children: 'Error loading data',
        //     }
        //     : undefined,
        muiTableContainerProps: {
            sx: {
                minHeight: '500px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                minHeight: 20,
                '& td': {
                    padding: '4px 8px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '4px 8px',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                textAlign: 'center',
                verticalAlign: 'middle',
                overflow: 'visible',
                textOverflow: 'unset',
                '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Wrapper': {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                },
            },
        },
        muiTableHeadRowProps: {
            sx: {
                height: 'auto',
                '& th': {
                    verticalAlign: 'middle',
                },
            },
        },
        onCreatingRowCancel: () => setValidationFailed(false),
        onEditingRowCancel: () => {
            setEditedRegisters({});
            setValidationFailed(false);
        },
        onCreatingRowSave: handleCreateRegister,
        renderRowActions: ({ row }) => (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Popup
                    type="hover"
                    triggerComponent={
                        <Button
                            iconLeft={"delete"}
                            status="neutral"
                            variant="ghost"
                            size="small"
                            onPress={() => handleDelete(row.original)}
                            disabled={!hasRights(['efe_registers_delete'])}
                        />
                    }
                >
                    <Surface style={{ padding: padding }} elevation={2}>
                        <Text>{t('delete')}</Text>
                    </Surface>
                </Popup>
            </div>
        ),
        renderBottomToolbarCustomActions: () => (
            <Surface style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                    status="primary"
                    onPress={handleSave}
                    disabled={Object.keys(editedRegisters).length === 0 || validationFailed || !hasRights(["efe_registers_edit"])}
                >
                    {t('save')}
                </Button>
                {validationFailed && (
                    <Alert severity="error" style={{ marginLeft: 8 }}>
                        {t('mandatoryFields')}
                    </Alert>
                )}
            </Surface>
        ),
        renderTopToolbarCustomActions: ({ table }) => (
            <Popup
                type='hover'
                triggerComponent={
                    <Button iconLeft={"add"} status='neutral' variant='ghost' disabled={!hasRights(["efe_registers_create"])} onPress={() => {
                        table.setCreatingRow(true); //simplest way to open the create row modal with no default values
                        //         //or you can pass in a row object to set default values with the `createRow` helper function
                        //         // table.setCreatingRow(
                        //         //   createRow(table, {
                        //         //     //optionally pass in default values for the new row, useful for nested data or other complex scenarios
                        //         //   }),
                        //         // );
                    }} />
                }
            >
                <Surface style={{ padding: padding }} elevation={2}>
                    <Text>{t('add')}</Text>
                </Surface>
            </Popup>
        )
    });

    return (
        <Surface style={{width: '100%'}}>
            <MaterialReactTable table={table} />

            <DeleteConfirmation open={deleteDialogOpen} onClose={closeDeleteDialog} onDelete={deleteReg} />
        </Surface>
    )
}

export default RegisterTestTable;